import { useCallback } from "react";
import { CourtGridTimeSlot } from "../../../models/CourtGridData";
import { SlotStatus } from "../../shared/TimeSlot";

interface UseSlotStatusProps {
  selectedDate: Date;
  exercisesInBasket: number[];
}

export default function useSlotStatus({ selectedDate, exercisesInBasket }: UseSlotStatusProps) {
  const isHourPastWithBuffer = useCallback(
    (timeString: string) => {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const selectedDateOnly = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());

      if (selectedDateOnly.getTime() !== today.getTime()) {
        return false;
      }

      const currentTime = now.getHours() * 60 + now.getMinutes();
      const [hours, minutes] = timeString.split(":").map(Number);
      const slotTime = hours * 60 + minutes;

      return slotTime <= currentTime;
    },
    [selectedDate]
  );

  const getSlotStatus = useCallback(
    (timeSlot: CourtGridTimeSlot): SlotStatus => {
      if (isHourPastWithBuffer(timeSlot.startTime)) {
        return "unavailable";
      }

      if (timeSlot.userAssigned) {
        return "assigned";
      }

      if (exercisesInBasket.includes(timeSlot.exerciseId)) {
        return "in-basket";
      }

      if (timeSlot.reserved >= timeSlot.capacity) {
        return "sold-out";
      }

      return "available";
    },
    [exercisesInBasket, isHourPastWithBuffer]
  );

  const visibleHours = useCallback(
    (hours: string[], showPastHours: boolean) => {
      return showPastHours ? hours : hours.filter((hour) => !isHourPastWithBuffer(hour));
    },
    [isHourPastWithBuffer]
  );

  const hasPastHours = useCallback(
    (hours: string[]) => {
      return hours.some((hour) => isHourPastWithBuffer(hour));
    },
    [isHourPastWithBuffer]
  );

  return {
    getSlotStatus,
    isHourPastWithBuffer,
    visibleHours,
    hasPastHours,
  };
}
